import { z } from 'zod';

/**
 * Contract for POST user/get-profile.
 *
 * SECURITY: the backend currently returns `password` (bcrypt hash), `salt` and
 * `rawPassword` in this response. This file is an explicit allow-list: only the
 * fields below are kept, so those secrets never reach React state, the query cache
 * or the DOM. The backend should stop sending them.
 */

/** 0/1 or true/false → boolean */
const flag = z.union([z.boolean(), z.literal(0), z.literal(1)]).transform((v) => v === true || v === 1);

const answersSchema = z
  .object({
    fuel_type: z.string().nullable().optional(),
    fuel_frequency: z.string().nullable().optional(),
  })
  .passthrough();

/** Allow-listed fields and how to read them. Anything not listed is dropped. */
const PROFILE_FIELDS = {
  id: z.union([z.number(), z.string()]),
  userId: z.string().min(1),
  name: z.string().nullable(),
  email: z.string().nullable(),
  phone_number: z.string().nullable(),
  isActive: flag.nullable(),
  createdAt: z.string().nullable(),
  scans_made: z.number().int().nonnegative().nullable(),
  has_answered: flag.nullable(),
  provider: z.string().nullable(),
  answers: answersSchema.nullable(),
};

const REQUIRED = ['userId'];

/**
 * @typedef {object} ProfileDto  Allow-listed profile; invalid or absent fields are null.
 * @property {number|string|null} id
 * @property {string|null} userId
 * @property {string|null} name
 * @property {string|null} email
 * @property {string|null} phone_number
 * @property {boolean|null} isActive
 * @property {string|null} createdAt
 * @property {number|null} scans_made
 * @property {boolean|null} has_answered
 * @property {string|null} provider
 * @property {{ fuel_type?: string|null, fuel_frequency?: string|null }|null} answers
 */

/**
 * Keeps only allow-listed fields, validating each one on its own so one bad value
 * blanks just that field. Returns `{ profile, invalidFields }`.
 * @returns {{ profile: ProfileDto, invalidFields: string[] }}
 */
export function parseProfile(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const profile = {};
  const invalidFields = [];
  for (const [field, schema] of Object.entries(PROFILE_FIELDS)) {
    if (source[field] === undefined && !REQUIRED.includes(field)) {
      profile[field] = null;
      continue;
    }
    const result = schema.safeParse(source[field]);
    if (result.success) {
      profile[field] = result.data ?? null;
    } else {
      profile[field] = null;
      invalidFields.push(field);
    }
  }
  return { profile, invalidFields };
}
